import type { Node, NodeProps } from '@xyflow/react';
import type React from 'react';
import { useMemo } from 'react';
import { useAppPermissionsContext } from '../../../context/app-permissions.context';
import { CriticalControlEnum, EffectiveControlEnum } from '../../../helpers/node-util';
import { cn, makeDataCy } from '../../../helpers/util';
import { useAppSelector } from '../../../redux/hooks';
import { selectCauses, selectControlNodeHasEscalationFactors, selectMoving } from '../../../redux/slices/diagram';
import BaseNode, { type BaseNodeData } from './base-node.component';
import AddEscalationFactorControl from './common/add-escalation-factor-control.component';
import HorizontalMoveControl, {
  HORIZONTAL_STEP,
  HorizontalMoveDirection,
} from './common/horizontal-move-control.component';

type PreventativeControlNodeData = BaseNodeData & {
  criticalControlType?: CriticalControlEnum;
  effectiveControlType?: EffectiveControlEnum;
};
export type PreventativeControlNodeType = Node<PreventativeControlNodeData>;
type PreventativeControlNodeProps = NodeProps<PreventativeControlNodeType>;

const defaultClassName = 'bg-pc-default-bg border-pc-default-border text-pc-default-fg';
const nonEffectiveClassName =
  'bg-pc-non_critical-non_effective-bg border-pc-non_critical-non_effective-border text-pc-non_critical-non_effective-fg';
const criticalClassName =
  'bg-pc-critical-effective-bg border-pc-critical-effective-border text-pc-critical-effective-fg';

const defaultIconClassName = 'text-pc-default-bg stroke-pc-default-fg';
const nonEffectiveIconClassName = 'text-pc-non_critical-non_effective-bg stroke-pc-non_critical-non_effective-fg';
const criticalIconClassName = 'text-pc-critical-effective-bg stroke-pc-critical-effective-fg';

const defaultGlobalIconClassName = 'text-pc-default-border';
const nonEffectiveGlobalIconClassName = 'text-pc-non_critical-non_effective-border';
const criticalGlobalIconClassName = 'text-pc-critical-effective-border';

/**
 * A component that represents a preventative control node in a flow diagram.
 * Extends the base node component with preventative-control-specific functionality.
 *
 * @component
 * @param {PreventativeControlNodeProps} props - The properties for the preventative control node component
 * @returns {JSX.Element} A rendered preventative control node component
 *
 * @example
 * ```tsx
 * <PreventativeControlNode
 *   id="preventative control-1"
 *   data={{ label: "Preventative Control" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const PreventativeControlNode = (props: PreventativeControlNodeProps): React.JSX.Element => {
  const { recordId, criticalControlType, effectiveControlType, cellIndex, rowIndex, editMode } = props.data;
  const causes = useAppSelector(selectCauses);
  const moving = useAppSelector(selectMoving);
  const hasEscalationFactors = useAppSelector((state) => selectControlNodeHasEscalationFactors(state, props.id));

  const {
    addNodePermission: { escalationFactors: canAddEscalationFactor },
  } = useAppPermissionsContext();

  const canMoveLeft = useMemo(
    () =>
      cellIndex !== undefined && rowIndex !== undefined && rowIndex >= 0 && rowIndex <= causes.length - 1
        ? cellIndex < causes[rowIndex].controls.length - HORIZONTAL_STEP
        : false,
    [cellIndex, rowIndex, causes]
  );

  const canMoveRight = useMemo(() => (cellIndex !== undefined ? cellIndex >= HORIZONTAL_STEP : false), [cellIndex]);

  const { className, iconClassName, globalIconClassName } = useMemo(() => {
    let className = defaultClassName;
    let iconClassName = defaultIconClassName;
    let globalIconClassName = defaultGlobalIconClassName;

    if (effectiveControlType === EffectiveControlEnum.NON_EFFECTIVE) {
      className = nonEffectiveClassName;
      iconClassName = nonEffectiveIconClassName;
      globalIconClassName = nonEffectiveGlobalIconClassName;
    } else if (criticalControlType === CriticalControlEnum.CRITICAL) {
      className = criticalClassName;
      iconClassName = criticalIconClassName;
      globalIconClassName = criticalGlobalIconClassName;
    }

    className = cn(className, { 'animate-wobble': moving && (canMoveLeft || canMoveRight) });

    return { className, iconClassName, globalIconClassName };
  }, [criticalControlType, effectiveControlType, canMoveLeft, canMoveRight, moving]);

  const data = useMemo(() => {
    return {
      ...props.data,
      className,
      iconClassName,
      globalIconClassName,
      showRecordIndicators: true,
      showUnlinkActionIcon: !props.data.recordId || !hasEscalationFactors,
    };
  }, [props.data, className]);

  const dataCyLabel = useMemo(() => makeDataCy(props.data.label ?? ''), [props.data.label]);

  return (
    <div data-cy={`parent-preventative-control-node-container-${dataCyLabel}`} className="group flex items-center">
      <HorizontalMoveControl
        id={props.id}
        type="preventative-control-node"
        index={cellIndex!}
        rowIndex={rowIndex!}
        title="Move Left"
        direction={HorizontalMoveDirection.LEFT}
        disabled={!moving || !canMoveLeft}
      />

      <BaseNode {...props} data={data} />

      <HorizontalMoveControl
        id={props.id}
        type="preventative-control-node"
        index={cellIndex!}
        rowIndex={rowIndex!}
        title="Move Right"
        direction={HorizontalMoveDirection.RIGHT}
        disabled={!moving || !canMoveRight}
      />

      <AddEscalationFactorControl
        id={props.id} // preventative control node id
        controlRecordId={recordId}
        index={cellIndex!}
        disabled={moving || editMode || !canAddEscalationFactor || hasEscalationFactors}
      />
    </div>
  );
};

export default PreventativeControlNode;
